import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from '@services/user.service';
import { INodeStructure, NodeType } from '@shared/interfaces';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ApiService, PATH } from 'src/app/core/api/api.service';
import CONSTANTS from 'src/app/util/Constant';

export interface ICatalogueIdName {
  id: string;
  name: string;
}

// GE-4543 UI rework. Three-column selector data. `id` is the full `NODE::<uuid>`.
export interface IStoreOption {
  id: string;
  name: string;
}

export interface IStoreGroupOption {
  id: string;
  name: string;
  stores: IStoreOption[];
}

export interface ITradingRegionOption {
  id: string;
  name: string;
  storeGroups: IStoreGroupOption[];
}

export interface IStoreHierarchy {
  companyName: string;
  tradingRegions: ITradingRegionOption[];
}

// Buckets for stores that sit outside the region -> group -> store path. Rendered with the
// `unassigned_stores` label and never written into `storeNodeIds`.
export const UNASSIGNED_GROUP_ID = '__unassigned_group__';
export const UNASSIGNED_REGION_ID = '__unassigned_region__';

const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name);

@Injectable()
export class GlideSettingsService {
  constructor(private apiService: ApiService, private userService: UserService) {}

  getCatalogueIdNameList(companyId: string): Observable<ICatalogueIdName[]> {
    return this.apiService
      .get(PATH.GET_CATALOGUE_ID_NAME_LIST, new HttpParams().set('companyId', companyId))
      .pipe(map((res) => (res?.result ?? res ?? []) as ICatalogueIdName[]));
  }

  getProductRecommendationList(companyId: string): Observable<ICatalogueIdName[]> {
    return this.apiService
      .post(PATH.POST_PRODUCT_RECOMMENDATION_LIST, {
        idNameOnly: true,
        companyIds: [companyId],
        limit: CONSTANTS.PRODUCT_RECOMMENDATION_PAGE_LIMIT,
        page: 1
      })
      .pipe(
        map((res) =>
          (res?.result ?? res ?? []).map((item: any) => ({ id: item.id, name: item.name }))
        )
      );
  }

  // GE-4543 UI rework. Still derived from the admin's node structure in auth state -- no new API.
  // getUserNodeStructure() is backed by a hot, never-completing store selector; take(1) keeps a
  // panel open/close cycle from accumulating subscriptions since callers don't tear this down.
  getCompanyStoreHierarchy(companyId: string): Observable<IStoreHierarchy> {
    return this.userService.getUserNodeStructure().pipe(
      take(1),
      map((nodeStructure: INodeStructure[]) => this.buildHierarchy(nodeStructure, companyId))
    );
  }

  private buildHierarchy(nodeStructure: INodeStructure[], companyId: string): IStoreHierarchy {
    if (!nodeStructure?.length || !companyId) {
      return { companyName: '', tradingRegions: [] };
    }

    const childrenByParent = new Map<string, INodeStructure[]>();
    nodeStructure.forEach((node) => {
      const siblings = childrenByParent.get(node.parentId) ?? [];
      siblings.push(node);
      childrenByParent.set(node.parentId, siblings);
    });

    const activeRegions = this.activeChildren(childrenByParent, companyId, NodeType.TRADING_REGION);
    const tradingRegions: ITradingRegionOption[] = activeRegions.map((region) => ({
      id: region.nodeId,
      name: region.name,
      storeGroups: this.buildStoreGroups(childrenByParent, region.nodeId)
    }));

    // A store parented straight to the company is one bucket-eligible case. The other is any
    // store sitting beneath an *inactive* trading region: that region is (correctly) not a
    // column-1 row, but its active stores must not be stranded, so they roll up here too.
    // Only the direct-store fast path is needed for a truly direct store; subtreeStores handles
    // the inactive-region case below since an inactive region itself is never traversed as an
    // "active" region above.
    const inactiveRegions = (childrenByParent.get(companyId) ?? []).filter(
      (node) => node.nodeType === NodeType.TRADING_REGION && !node.status
    );
    const companyDirectStores = [
      ...this.directStores(childrenByParent, companyId),
      ...inactiveRegions.flatMap((region) => this.subtreeStores(childrenByParent, region.nodeId))
    ].sort(byName);
    if (companyDirectStores.length) {
      tradingRegions.push({
        id: UNASSIGNED_REGION_ID,
        name: '',
        storeGroups: [{ id: UNASSIGNED_GROUP_ID, name: '', stores: companyDirectStores }]
      });
    }

    const company = nodeStructure.find((node) => node.nodeId === companyId);
    return { companyName: company?.name ?? '', tradingRegions };
  }

  private buildStoreGroups(
    childrenByParent: Map<string, INodeStructure[]>,
    regionId: string
  ): IStoreGroupOption[] {
    const activeGroups = this.activeChildren(childrenByParent, regionId, NodeType.STORE_GROUP);
    const groups: IStoreGroupOption[] = activeGroups.map((group) => ({
      id: group.nodeId,
      name: group.name,
      // Whole subtree: a "sub store group" is persisted as a STORE_GROUP under a STORE_GROUP, so
      // its stores must roll up here or they can never be ticked.
      stores: this.subtreeStores(childrenByParent, group.nodeId)
    }));

    // Same bucket logic as the company level, one rung down: a store parented straight to the
    // region, plus any active store beneath an *inactive* store group in this region (that group
    // is correctly not a column-2 row, but its active stores must still be reachable).
    const inactiveGroups = (childrenByParent.get(regionId) ?? []).filter(
      (node) => node.nodeType === NodeType.STORE_GROUP && !node.status
    );
    const regionDirectStores = [
      ...this.directStores(childrenByParent, regionId),
      ...inactiveGroups.flatMap((group) => this.subtreeStores(childrenByParent, group.nodeId))
    ].sort(byName);
    if (regionDirectStores.length) {
      groups.push({ id: UNASSIGNED_GROUP_ID, name: '', stores: regionDirectStores });
    }

    return groups;
  }

  private activeChildren(
    childrenByParent: Map<string, INodeStructure[]>,
    parentId: string,
    nodeType: NodeType
  ): INodeStructure[] {
    return (childrenByParent.get(parentId) ?? [])
      .filter((node) => node.nodeType === nodeType && node.status)
      .sort(byName);
  }

  private directStores(
    childrenByParent: Map<string, INodeStructure[]>,
    parentId: string
  ): IStoreOption[] {
    return this.activeChildren(childrenByParent, parentId, NodeType.STORE).map((node) => ({
      id: node.nodeId,
      name: node.name
    }));
  }

  // Walks the whole subtree under rootId collecting active stores. Status is checked ONLY at
  // NodeType.STORE: an inactive intermediate (STORE_GROUP or TRADING_REGION) must not appear as
  // a column row, but it must not act as a wall either -- an active store beneath it is still
  // live and still sitting in storeNodeIds, so it must stay reachable somewhere in the UI.
  // Callers are responsible for routing an inactive intermediate's own subtree into the right
  // bucket instead of calling this on it as if it were a normal active group/region.
  private subtreeStores(
    childrenByParent: Map<string, INodeStructure[]>,
    rootId: string
  ): IStoreOption[] {
    const stores = new Map<string, IStoreOption>();
    const queue: string[] = [rootId];
    const seen = new Set<string>([rootId]);

    while (queue.length) {
      const parentId = queue.shift() as string;
      (childrenByParent.get(parentId) ?? []).forEach((node) => {
        if (seen.has(node.nodeId)) {
          return;
        }
        seen.add(node.nodeId);
        if (node.nodeType === NodeType.STORE) {
          if (node.status) {
            stores.set(node.nodeId, { id: node.nodeId, name: node.name });
          }
          return;
        }
        // Traverse through inactive non-store nodes rather than stopping at them.
        queue.push(node.nodeId);
      });
    }

    return [...stores.values()].sort(byName);
  }
}
